import React, { useState, useEffect } from "react";
import {
  Search,
  Heart,
  ShoppingCart,
  User,
  Trash2,
  Plus,
  Minus,
  PlusCircle,
  Download,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getFirestore,
  doc,
  collection,
  getDocs,
  deleteDoc,
  updateDoc,
  addDoc,
  query,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  Alert,
  AlertTitle,
  Paper,
  Typography,
  Divider,
  Box,
} from "@mui/material";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { getUserId } from "../utils/utils";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { useRazorpay, RazorpayOrderOptions } from "react-razorpay";

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const { error, isLoadingPayment, Razorpay } = useRazorpay();

  const [newAddress, setNewAddress] = useState({
    label: "",
    street: "",
    city: "",
    state: "",
    pincode: "",
  });

  const navigate = useNavigate();
  const db = getFirestore();
  const auth = getAuth();

  useEffect(() => {
    const fetchCartItems = async () => {
      try {
        const userId = getUserId();
        const cartRef = collection(db, `users/${userId}/cart`);
        const snapshot = await getDocs(cartRef);
        const items = snapshot.docs.map((doc) => ({
          itemId: doc.id,
          ...doc.data(),
        }));
        setCartItems(items);
        calculateTotal(items);
      } catch (error) {
        console.error("Error fetching cart data:", error);
      }
    };

    const fetchAddresses = async () => {
      try {
        const userId = getUserId();
        const addressesRef = collection(db, `users/${userId}/addresses`);
        const snapshot = await getDocs(addressesRef);
        const addressList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAddresses(addressList);
        if (addressList.length > 0) {
          setSelectedAddress(addressList[0]);
        }
      } catch (error) {
        console.error("Error fetching addresses:", error);
      }
    };

    fetchCartItems();
    fetchAddresses();
  }, [db, auth]);

  useEffect(() => {
    if (cartItems && cartItems?.length > 0) {
      calculateTotal(cartItems);
    }
  }, [cartItems]);

  useEffect(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, []);

  const calculateTotal = (items) => {
    const total = items?.reduce(
      (acc, item) => acc + item.price * item.quantity,
      0
    );
    setTotalAmount(total);
  };

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    try {
      const userId = getUserId();
      const itemRef = doc(db, `users/${userId}/cart`, itemId);
      await updateDoc(itemRef, { quantity: newQuantity });
      setCartItems((prevItems) =>
        prevItems.map((item) =>
          item.itemId === itemId ? { ...item, quantity: newQuantity } : item
        )
      );
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      const userId = getUserId();
      const itemRef = doc(db, `users/${userId}/cart`, itemId);
      await deleteDoc(itemRef);
      const updatedCart = cartItems.filter((item) => item.itemId !== itemId);
      setCartItems(updatedCart);
      calculateTotal(updatedCart);
    } catch (error) {
      console.error("Error removing item:", error);
    }
  };

  const handleAddAddress = async () => {
    try {
      const userId = getUserId();
      const addressesRef = collection(db, `users/${userId}/addresses`);
      const addressDetail = {
        label: newAddress.label,
        street: newAddress.street,
        city: newAddress.city,
        state: newAddress.state,
        pincode: newAddress.pincode,
        detail: `${newAddress.street}, ${newAddress.city}, ${newAddress.state}, ${newAddress.pincode}`,
      };

      const docRef = await addDoc(addressesRef, addressDetail);
      const newAddressObj = {
        id: docRef.id,
        ...addressDetail,
      };

      setAddresses([...addresses, newAddressObj]);
      setSelectedAddress(newAddressObj);
      setIsAddingAddress(false);
      setNewAddress({
        label: "",
        street: "",
        city: "",
        state: "",
        pincode: "",
      });
    } catch (error) {
      console.error("Error adding address:", error);
    }
  };

  const generatePDFReceipt = async () => {
    try {
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([595.28, 841.89]); // A4 size
      const { width, height } = page.getSize();
      const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

      // Header
      page.drawText("Your Store Name", {
        x: 50,
        y: height - 50,
        size: 24,
        font,
        color: rgb(0.49, 0.23, 0.93), // Purple color
      });

      // Store Details
      page.drawText("123 Store Street, City - 123456", {
        x: 50,
        y: height - 70,
        size: 10,
        font: regularFont,
      });

      // Order Details
      page.drawText(`Order Receipt`, {
        x: 50,
        y: height - 120,
        size: 16,
        font,
      });

      page.drawText(`Order ID: ${orderId}`, {
        x: 50,
        y: height - 140,
        size: 12,
        font: regularFont,
      });

      page.drawText(`Date: ${new Date().toLocaleDateString()}`, {
        x: 50,
        y: height - 160,
        size: 12,
        font: regularFont,
      });

      // Customer Details
      page.drawText("Shipping Address:", {
        x: 50,
        y: height - 200,
        size: 14,
        font,
      });

      const addressLines = selectedAddress.detail.split(",");
      addressLines.forEach((line, index) => {
        page.drawText(line.trim(), {
          x: 50,
          y: height - 220 - index * 20,
          size: 12,
          font: regularFont,
        });
      });

      // Items Table Header
      const tableTop = height - 300;
      page.drawText("Item", { x: 50, y: tableTop, size: 12, font });
      page.drawText("Qty", { x: 300, y: tableTop, size: 12, font });
      page.drawText("Price", { x: 400, y: tableTop, size: 12, font });
      page.drawText("Total", { x: 500, y: tableTop, size: 12, font });

      // Items
      let currentY = tableTop - 30;
      orderDetails?.items?.forEach((item) => {
        page.drawText(item.name, {
          x: 50,
          y: currentY,
          size: 10,
          font: regularFont,
        });
        page.drawText(item.quantity.toString(), {
          x: 300,
          y: currentY,
          size: 10,
          font: regularFont,
        });
        page.drawText(`Rs. ${item.price}`, {
          x: 400,
          y: currentY,
          size: 10,
          font: regularFont,
        });
        page.drawText(`Rs. ${item.price * item.quantity}`, {
          x: 500,
          y: currentY,
          size: 10,
          font: regularFont,
        });
        currentY -= 20;
      });

      // Total
      page.drawText("Total Amount:", {
        x: 400,
        y: currentY - 30,
        size: 12,
        font,
      });
      page.drawText(`Rs.${totalAmount}`, {
        x: 500,
        y: currentY - 30,
        size: 12,
        font,
      });

      // Footer
      page.drawText("Thank you for shopping with us!", {
        x: 50,
        y: 50,
        size: 14,
        font,
      });

      page.drawText(
        "For any queries, contact us at: support@yourstore.com | +91-1234567890",
        {
          x: 50,
          y: 30,
          size: 10,
          font: regularFont,
        }
      );

      // Save PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-receipt-${orderId}.pdf`;
      a.click();
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handlePaymentMethodSelection = () => {
    setShowPaymentModal(true);
  };

  const initializeRazorpay = async () => {
    try {
      const options = {
        key: "rzp_test_qi4e27ea7mriO4",
        // order_id: order.id,
        name: "Ambaari Jewels",
        description: "Transaction for your jewelleries",
        amount: totalAmount * 100,
        currency: "INR",
        prefill: {
          name: "John Doe",
          email: "john.doe@example.com",
          contact: "9999999999",
        },
        theme: {
          color: "#7C3AED",
        },
        handler: (response) => {
          console.log(response);
          handleOrderPlacement("online", response.razorpay_payment_id);
        },
      };

      const razorpayInstance = new Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      console.error("Payment error:", error);
    }
  };

  const handleOrderPlacement = async (paymentMethod, paymentId = null) => {
    try {
      const userId = getUserId();
      const ordersRef = collection(db, "orders");

      const orderData = {
        userId,
        items: cartItems,
        address: selectedAddress,
        totalAmount,
        paymentMethod,
        paymentId,
        status: "pending",
        orderDate: new Date().toISOString(),
        orderedOn: new Date(),
      };

      const orderDoc = await addDoc(ordersRef, orderData);
      setOrderId(orderDoc.id);
      setOrderDetails(orderData);

      // Clear cart
      const cartRef = collection(db, `users/${userId}/cart`);
      const snapshot = await getDocs(cartRef);
      snapshot.docs.forEach(async (doc) => {
        await deleteDoc(doc.ref);
      });

      setCartItems([]);
      setOrderSuccess(true);
      setShowPaymentModal(false);
    } catch (error) {
      console.error("Error placing order:", error);
    }
  };

  const OrderSuccessView = () => (
    <div className="container mx-auto px-6 py-12">
      <Alert
        severity="success"
        sx={{
          mb: 3,
          "& .MuiAlert-message": {
            width: "100%",
          },
        }}
      >
        <AlertTitle>Order Placed Successfully!</AlertTitle>
        Thank you for your order. Our customer service team will contact you
        shortly.
      </Alert>

      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Order Summary
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Order ID: {orderId}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Order Date: {new Date().toLocaleDateString()}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Shipping Address
          </Typography>
          <Typography variant="body1">{selectedAddress?.detail}</Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Items
          </Typography>
          {orderDetails?.items?.map((item) => (
            <Box
              key={item.itemId}
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography>
                {item.name} x {item.quantity}
              </Typography>
              <Typography>₹{item.price * item.quantity}</Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h6">Total Amount</Typography>
          <Typography variant="h6">₹{totalAmount}</Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <button
            onClick={generatePDFReceipt}
            className="flex items-center gap-2 px-6 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-600"
          >
            <Download className="w-4 h-4" />
            Download Receipt
          </button>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
          >
            Continue Shopping
          </button>
        </Box>
      </Paper>
    </div>
  );
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-50 via-white to-purple-50">
      <Header />

      {orderSuccess ? (
        <OrderSuccessView />
      ) : (
        <div className="container mx-auto px-6 py-12">
          <h2 className="text-3xl font-serif text-center mb-12 text-purple-900">
            Shopping Cart
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items Section */}
            <div className="lg:col-span-2 space-y-6">
              {cartItems.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl shadow-md">
                  <p className="text-purple-800 mb-4">Your cart is empty</p>
                  <button
                    onClick={() => navigate("/search")}
                    className="px-6 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-600 transition-colors duration-200 transform hover:scale-105"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                cartItems.map((item) => (
                  <div
                    key={item.itemId}
                    className="bg-white rounded-xl shadow-md p-6 flex items-center gap-6"
                  >
                    <img
                      src={item?.images[0]}
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-grow">
                      <h3 className="text-lg font-medium text-purple-900">
                        {item.name}
                      </h3>
                      <p className="text-purple-600 mb-4">
                        {item?.category?.name}
                      </p>
                      <div className="flex items-center justify-between">
                        <p className="text-xl font-serif text-purple-800">
                          ₹{item?.price}
                        </p>
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() =>
                              handleUpdateQuantity(
                                item.itemId,
                                Math.max(0, item.quantity - 1)
                              )
                            }
                            className="p-1 rounded-full hover:bg-purple-50 text-purple-700"
                          >
                            <Minus className="w-5 h-5" />
                          </button>
                          <span className="w-8 text-center text-purple-900">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              handleUpdateQuantity(
                                item.itemId,
                                item.quantity + 1
                              )
                            }
                            className="p-1 rounded-full hover:bg-purple-50 text-purple-700"
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleRemoveItem(item.itemId)}
                            className="p-1 rounded-full hover:bg-red-50 text-red-500"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Order Summary Section */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
                <h3 className="text-xl font-serif mb-6 text-purple-900">
                  Order Summary
                </h3>

                {/* Address Selection */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-purple-800">
                      Delivery Address
                    </h4>
                    <button
                      onClick={() => setIsAddingAddress(true)}
                      className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
                    >
                      <PlusCircle className="w-4 h-4" />
                      Add New
                    </button>
                  </div>

                  {isAddingAddress ? (
                    <div className="space-y-3 mb-4">
                      <input
                        type="text"
                        placeholder="Address Label (e.g., Home, Office)"
                        className="w-full p-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={newAddress.label}
                        onChange={(e) =>
                          setNewAddress({
                            ...newAddress,
                            label: e.target.value,
                          })
                        }
                      />
                      <input
                        type="text"
                        placeholder="Street Address"
                        className="w-full p-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={newAddress.street}
                        onChange={(e) =>
                          setNewAddress({
                            ...newAddress,
                            street: e.target.value,
                          })
                        }
                      />
                      <input
                        type="text"
                        placeholder="City"
                        className="w-full p-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={newAddress.city}
                        onChange={(e) =>
                          setNewAddress({ ...newAddress, city: e.target.value })
                        }
                      />
                      <input
                        type="text"
                        placeholder="State"
                        className="w-full p-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={newAddress.state}
                        onChange={(e) =>
                          setNewAddress({
                            ...newAddress,
                            state: e.target.value,
                          })
                        }
                      />
                      <input
                        type="text"
                        placeholder="PIN Code"
                        className="w-full p-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        value={newAddress.pincode}
                        onChange={(e) =>
                          setNewAddress({
                            ...newAddress,
                            pincode: e.target.value,
                          })
                        }
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleAddAddress}
                          className="flex-1 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 transform hover:scale-105"
                        >
                          Save Address
                        </button>
                        <button
                          onClick={() => setIsAddingAddress(false)}
                          className="flex-1 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-all duration-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {addresses.map((address) => (
                        <div
                          key={address.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                            selectedAddress?.id === address.id
                              ? "border-purple-700 bg-purple-50"
                              : "border-purple-200 hover:border-purple-400"
                          }`}
                          onClick={() => setSelectedAddress(address)}
                        >
                          <p className="font-medium text-sm text-purple-900">
                            {address.label}
                          </p>
                          <p className="text-sm text-purple-600">
                            {address.detail}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Price Details */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm text-purple-800">
                    <span>Subtotal</span>
                    <span>₹{totalAmount}</span>
                  </div>
                  <div className="flex justify-between text-sm text-purple-800">
                    <span>Shipping</span>
                    <span>Free</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium pt-3 border-t border-purple-100">
                    <span className="text-purple-900">Total</span>
                    <span className="text-purple-900">₹{totalAmount}</span>
                  </div>
                </div>

                <button
                  onClick={handlePaymentMethodSelection}
                  disabled={!selectedAddress || cartItems.length === 0}
                  className="w-full py-3 bg-purple-700 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 transform hover:scale-105 shadow-lg disabled:bg-purple-300 disabled:cursor-not-allowed"
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-serif mb-6 text-purple-900">
              Select Payment Method
            </h3>
            <div className="space-y-4">
              <button
                onClick={() => initializeRazorpay()}
                className="w-full py-3 bg-purple-700 text-white rounded-lg hover:bg-purple-600 transition-all duration-200"
              >
                Pay Online (Razorpay)
              </button>
              <button
                onClick={() => handleOrderPlacement("cod")}
                className="w-full py-3 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-all duration-200"
              >
                Cash on Delivery
              </button>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="w-full py-3 border border-purple-200 text-purple-600 rounded-lg hover:bg-purple-50 transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CartPage;
